import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryStub } from '../../utils';
import {
  DueDatesDto,
  EngineeringFeesDto,
  NotificationsDto,
  PaintSettingsDto,
  SystemSettingsService,
} from './system-settings-service';

// A helper function to initialize the service once when needed
let systemSettingsService: SystemSettingsService | null = null;
const getSystemSettingsService = () => {
  systemSettingsService ??= new SystemSettingsService();
  return systemSettingsService;
};

// Define an API service using RTK Query that uses the SystemSettingsService
export const systemSettingsApi = createApi({
  reducerPath: 'systemSettingsApi',
  baseQuery: baseQueryStub,
  tagTypes: [
    'DueDatesSettings',
    'NotificationsSettings',
    'EngineeringFeesSettings',
    'PaintSettings',
  ],
  endpoints: builder => ({
    // Due Dates Settings
    getDueDatesSettings: builder.query<DueDatesDto | null, number | undefined>({
      queryFn: async manufacturerId => {
        const service = getSystemSettingsService();
        const data = await service.getDueDatesSettings(manufacturerId);
        return { data };
      },
      providesTags: (_result, _, manufacturerId) => [
        { type: 'DueDatesSettings', id: manufacturerId || 'default' },
      ],
      keepUnusedDataFor: 60, // 60 seconds
    }),
    updateDueDatesSettings: builder.mutation<
      boolean,
      { manufacturerId: number | undefined; dueDates: Omit<DueDatesDto, 'manufacturerId'> }
    >({
      queryFn: async ({ manufacturerId, dueDates }) => {
        const service = getSystemSettingsService();
        const data = await service.updateDueDatesSettings(manufacturerId, dueDates);
        return { data };
      },
      async onQueryStarted({ manufacturerId, dueDates }, { dispatch, queryFulfilled }) {
        // Optimistically update the cache immediately
        const patchResult = dispatch(
          systemSettingsApi.util.updateQueryData('getDueDatesSettings', manufacturerId, draft => {
            if (draft) {
              draft.leadTime = dueDates.leadTime;
              draft.deliveryWindow = dueDates.deliveryWindow;
              draft.maxLinearFeet = dueDates.maxLinearFeet;
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          // On error, revert the optimistic update
          patchResult.undo();
        }
      },
      invalidatesTags: (_, __, { manufacturerId }) => [
        { type: 'DueDatesSettings', id: manufacturerId || 'default' },
      ],
    }),

    // Notifications Settings
    getNotificationsSettings: builder.query<NotificationsDto | null, number | undefined>({
      queryFn: async manufacturerId => {
        const service = getSystemSettingsService();
        const data = await service.getNotificationSettings(manufacturerId);
        return { data };
      },
      providesTags: (_result, _, manufacturerId) => [
        { type: 'NotificationsSettings', id: manufacturerId || 'default' },
      ],
      keepUnusedDataFor: 60, // 60 seconds
    }),
    updateNotificationsSettings: builder.mutation<
      boolean,
      {
        manufacturerId: number | undefined;
        notifications: Omit<NotificationsDto, 'manufacturerId'>;
      }
    >({
      queryFn: async ({ manufacturerId, notifications }) => {
        const service = getSystemSettingsService();
        const data = await service.updateNotificationSettings(manufacturerId, notifications);
        return { data };
      },
      async onQueryStarted({ manufacturerId, notifications }, { dispatch, queryFulfilled }) {
        // Optimistically update the cache immediately
        const patchResult = dispatch(
          systemSettingsApi.util.updateQueryData(
            'getNotificationsSettings',
            manufacturerId,
            draft => {
              if (draft) {
                draft.emailList = notifications.emailList;
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          // On error, revert the optimistic update
          patchResult.undo();
        }
      },
      invalidatesTags: (_, __, { manufacturerId }) => [
        { type: 'NotificationsSettings', id: manufacturerId || 'default' },
      ],
    }),

    // Engineering Fees Settings
    getEngineeringFeesSettings: builder.query<EngineeringFeesDto | null, number | undefined>({
      queryFn: async manufacturerId => {
        const service = getSystemSettingsService();
        const data = await service.getEngineeringFeesSettings(manufacturerId);
        return { data };
      },
      providesTags: (_result, _, manufacturerId) => [
        { type: 'EngineeringFeesSettings', id: manufacturerId || 'default' },
      ],
      keepUnusedDataFor: 60, // 60 seconds
    }),
    updateEngineeringFeesSettings: builder.mutation<
      boolean,
      {
        manufacturerId: number | undefined;
        engineeringFees: Omit<EngineeringFeesDto, 'manufacturerId'>;
      }
    >({
      queryFn: async ({ manufacturerId, engineeringFees }) => {
        const service = getSystemSettingsService();
        const data = await service.updateEngineeringFeesSettings(manufacturerId, engineeringFees);
        return { data };
      },
      async onQueryStarted({ manufacturerId, engineeringFees }, { dispatch, queryFulfilled }) {
        // Optimistically update the cache immediately
        const patchResult = dispatch(
          systemSettingsApi.util.updateQueryData(
            'getEngineeringFeesSettings',
            manufacturerId,
            draft => {
              if (draft) {
                draft.openPoleBarnFee = engineeringFees.openPoleBarnFee;
                draft.partialClosedPoleBarnFee = engineeringFees.partialClosedPoleBarnFee;
                draft.fullyEnclosedPoleBarnFee = engineeringFees.fullyEnclosedPoleBarnFee;
                draft.permanentDwellingFee = engineeringFees.permanentDwellingFee;
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          // On error, revert the optimistic update
          patchResult.undo();
        }
      },
      invalidatesTags: (_, __, { manufacturerId }) => [
        { type: 'EngineeringFeesSettings', id: manufacturerId || 'default' },
      ],
    }),

    // Paint Configuration Settings
    getPaintSettings: builder.query<PaintSettingsDto | null, number | undefined>({
      queryFn: async manufacturerId => {
        const service = getSystemSettingsService();
        const data = await service.getPaintConfigurationSettings(manufacturerId);
        return { data };
      },
      providesTags: (_result, _, manufacturerId) => [
        { type: 'PaintSettings', id: manufacturerId || 'default' },
      ],
      keepUnusedDataFor: 60, // 60 seconds
    }),
    updatePaintSettings: builder.mutation<
      boolean,
      {
        manufacturerId: number | undefined;
        paintSettings: Omit<PaintSettingsDto, 'manufacturerId'>;
      }
    >({
      queryFn: async ({ manufacturerId, paintSettings }) => {
        const service = getSystemSettingsService();
        const data = await service.updatePaintConfigurationSettings(manufacturerId, paintSettings);
        return { data };
      },
      async onQueryStarted({ manufacturerId, paintSettings }, { dispatch, queryFulfilled }) {
        // Optimistically update the cache immediately
        const patchResult = dispatch(
          systemSettingsApi.util.updateQueryData('getPaintSettings', manufacturerId, draft => {
            if (draft) {
              draft.surfaceAreaPerCoveredPerGallon = paintSettings.surfaceAreaPerCoveredPerGallon;
              draft.surfaceAreaPerLinearFoot = paintSettings.surfaceAreaPerLinearFoot;
              draft.markupPercentage = paintSettings.markupPercentage;
              draft.workHoursPerMonth = paintSettings.workHoursPerMonth;
              draft.utilityCostList = paintSettings.utilityCostList;
              draft.packagingCostList = paintSettings.packagingCostList;
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          // On error, revert the optimistic update
          patchResult.undo();
        }
      },
      invalidatesTags: (_, __, { manufacturerId }) => [
        { type: 'PaintSettings', id: manufacturerId || 'default' },
      ],
    }),
  }),
});

export const {
  useGetDueDatesSettingsQuery,
  useUpdateDueDatesSettingsMutation,
  useGetNotificationsSettingsQuery,
  useUpdateNotificationsSettingsMutation,
  useGetEngineeringFeesSettingsQuery,
  useUpdateEngineeringFeesSettingsMutation,
  useGetPaintSettingsQuery,
  useUpdatePaintSettingsMutation,
} = systemSettingsApi;
